import { ReactNode, useContext, useMemo } from 'react';

import styled from 'styled-components';

import { useDate } from '~/libs/hooks/useDate';
import { useProjectContext } from '~/modules/projects/context/ProjectProvider';

import { ReportIconCloseCircle } from '../ReportList';
import { ReportListContext } from '../ReportList/ReportListContext';

import { getFilterProcessTagInfo, getProcessText } from './ReportListFilter.helper';

export const ReportListFilterTags = () => {
  const { tab, filter, setFilter } = useContext(ReportListContext);
  const { project } = useProjectContext();
  const { formatDate } = useDate();

  const parsedProcesses = useMemo(() => {
    if (!filter.processes) return [];
    if (Array.isArray(filter.processes)) {
      return filter.processes;
    }
    return [filter.processes];
  }, [filter.processes]);

  const displayDate = (value?: string) => {
    if (!value) return '';
    return formatDate(value);
  };

  if (!filter.endWorkDate && !filter.startWorkDate && !parsedProcesses?.length) return null;

  return (
    <TagsWrap>
      {parsedProcesses.map((id) => {
        const tagInfo = getFilterProcessTagInfo(id, project?.processes);
        return (
          <FilterTag
            key={id}
            disabled={tab === 'timelapses'}
            onRemove={() =>
              setFilter({
                processes: parsedProcesses.filter((e) => e !== id),
              })
            }
          >
            {getProcessText(tagInfo?.processName, tagInfo?.taskName)}
          </FilterTag>
        );
      })}

      {(!!filter.startWorkDate || !!filter.endWorkDate) && (
        <FilterTag
          onRemove={() =>
            setFilter({
              startWorkDate: undefined,
              endWorkDate: undefined,
            })
          }
        >
          {displayDate(filter.startWorkDate)} ~ {displayDate(filter.endWorkDate)}
        </FilterTag>
      )}
    </TagsWrap>
  );
};

const TagsWrap = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  padding: 12px 16px;
  margin-top: -2px;
  gap: 6px;
  background: #f5f5f5;
  border-bottom: 1px solid #eeeeee;
`;

interface FilterTagProps {
  children: ReactNode;
  disabled?: boolean;
  onRemove: () => void;
}

const FilterTag = (props: FilterTagProps) => {
  const { children, disabled, onRemove } = props;
  return (
    <TagButton disabled={disabled} onClick={onRemove}>
      {children}
      <ReportIconCloseCircle
        color="var(--color-primary-400)"
        style={{
          width: 12,
          height: 12,
          marginLeft: 8,
        }}
      />
    </TagButton>
  );
};

const TagButton = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  height: 28px;
  padding: 0 10px;

  color: var(--color-primary-400);
  font-size: 14px;
  font-weight: 500;
  background-color: white;
  border: 1px solid var(--color-primary-400);
  border-radius: 4px;

  ${({ disabled }) => {
    if (!disabled) return '';
    return `
        opacity: 0.5;
      `;
  }}
`;
